import {
  AtSymbolIcon,
  BoltIcon,
  ChartBarSquareIcon,
  Cog6ToothIcon,
  KeyIcon,
  ShieldCheckIcon,
  SignalIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  canAccessUserManagement,
  isPlatformAdmin,
  NAV_FEATURE_MAP,
  userHasFeature,
} from './features';

/** Hub sidebar sections (outside workspace Layout). */
export const HUB_NAV_SECTION_IDS = {
  STORES: 'stores',
  ORGANIZATION: 'organization',
  SYSTEM: 'system',
  ACCOUNT: 'account',
};

const NAV_ITEMS = [
  {
    key: 'user-management',
    section: HUB_NAV_SECTION_IDS.ORGANIZATION,
    label: 'User Management',
    icon: UserGroupIcon,
    userManagementOnly: true,
    resolvePath: ({ qcHubActive }) =>
      qcHubActive
        ? '/user-management?tab=quick_commerce'
        : '/user-management?tab=amazon',
  },
  {
    key: 'qc-portal-accounts',
    section: HUB_NAV_SECTION_IDS.ORGANIZATION,
    label: 'QC portal accounts',
    icon: AtSymbolIcon,
    userManagementOnly: true,
    path: '/admin/qc-portal-accounts',
  },
  {
    key: 'access-hub',
    section: HUB_NAV_SECTION_IDS.ORGANIZATION,
    label: 'Access Hub',
    icon: ShieldCheckIcon,
    platformAdminOnly: true,
    path: '/admin/access-hub',
  },
  {
    key: 'settings',
    section: HUB_NAV_SECTION_IDS.ORGANIZATION,
    label: 'Store settings',
    icon: Cog6ToothIcon,
    platformAdminOnly: true,
    path: '/settings',
  },
  {
    key: 'data-sync',
    section: HUB_NAV_SECTION_IDS.SYSTEM,
    label: 'Data Sync',
    icon: ChartBarSquareIcon,
    platformAdminOnly: true,
    path: '/admin/unified-reporting',
  },
  {
    key: 'marketing-stream',
    section: HUB_NAV_SECTION_IDS.SYSTEM,
    label: 'Marketing Stream',
    icon: BoltIcon,
    platformAdminOnly: true,
    path: '/admin/marketing-stream',
  },
  {
    key: 'api-health',
    section: HUB_NAV_SECTION_IDS.SYSTEM,
    label: 'API Health',
    icon: SignalIcon,
    platformAdminOnly: true,
    path: '/admin/api-health',
  },
  {
    key: 'token-refresh',
    section: HUB_NAV_SECTION_IDS.SYSTEM,
    label: 'Token Refresh',
    icon: KeyIcon,
    platformAdminOnly: true,
    path: '/token-refresh',
  },
  {
    key: 'account',
    section: HUB_NAV_SECTION_IDS.ACCOUNT,
    label: 'Account & password',
    icon: UserCircleIcon,
    path: '/account',
  },
];

const SECTION_META = {
  [HUB_NAV_SECTION_IDS.ORGANIZATION]: { label: 'Team & access' },
  [HUB_NAV_SECTION_IDS.SYSTEM]: { label: 'Platform ops' },
  [HUB_NAV_SECTION_IDS.ACCOUNT]: { label: 'Account' },
};

const SIDEBAR_SECTION_ORDER = [
  HUB_NAV_SECTION_IDS.ORGANIZATION,
  HUB_NAV_SECTION_IDS.SYSTEM,
  HUB_NAV_SECTION_IDS.ACCOUNT,
];

function itemVisible(item, user) {
  if (!user) return false;
  const role = user.role;
  const permissions = user.permissions || [];
  const platformAdmin = isPlatformAdmin(role);

  if (item.platformAdminOnly && !platformAdmin) return false;
  if (item.userManagementOnly && !canAccessUserManagement(role)) return false;

  const feature = NAV_FEATURE_MAP[item.label];
  if (feature && !platformAdmin && !userHasFeature(permissions, feature)) {
    return false;
  }
  return true;
}

function resolvePath(item, opts) {
  if (item.resolvePath) return item.resolvePath(opts);
  return item.path;
}

/**
 * Grouped hub navigation for StoreHubSidebar (role-filtered, paths resolved).
 */
export function getStoreHubNavSections(user, { qcHubActive = false } = {}) {
  const opts = { qcHubActive };
  const resolved = NAV_ITEMS.filter((item) => itemVisible(item, user)).map((item) => ({
    ...item,
    path: resolvePath(item, opts),
  }));

  return SIDEBAR_SECTION_ORDER
    .map((sectionId) => ({
      id: sectionId,
      label: SECTION_META[sectionId]?.label ?? null,
      items: resolved.filter((i) => i.section === sectionId),
    }))
    .filter((s) => s.items.length > 0);
}

export function hasStoreHubAdminNav(user, opts = {}) {
  return getStoreHubNavSections(user, opts).some((s) => s.items.length > 0);
}

/** @deprecated Use getStoreHubNavSections */
export const STORE_HUB_ADMIN_NAV = NAV_ITEMS;

/** @deprecated Use getStoreHubNavSections */
export function visibleStoreHubAdminNav(user, opts) {
  return getStoreHubNavSections(user, opts).flatMap((s) => s.items);
}

/** @deprecated */
export const STORE_HUB_SETTINGS = NAV_ITEMS.filter((i) =>
  ['user-management', 'account', 'settings'].includes(i.key)
);

export function isStoreHubNavItemActive(pathname, searchParams, itemPath) {
  const base = String(itemPath).split('?')[0];
  if (base === '/user-management') {
    if (pathname !== '/user-management') return false;
    const want = new URLSearchParams(String(itemPath).split('?')[1] || '').get('tab');
    const cur = searchParams?.get?.('tab') || 'amazon';
    if (want === 'quick_commerce') return cur === 'quick_commerce';
    return cur !== 'quick_commerce';
  }
  return pathname === base || pathname.startsWith(`${base}/`);
}
